/* eslint brace-style: ["error", "allman"] */

const libFableServiceProviderBase = require('fable-serviceproviderbase');

/**
 * Reads default to a cap of 250 records (`MeadowDefaultMaxCap`), so every batch
 * read has to state its own cap or the tail of a large IN list is silently
 * dropped.
 */
const DEFAULT_BATCH_SIZE = 100;

/**
 * The body-driven `POST <Entity>s/Query` read carries the filter outside the
 * URI, so batches there are bounded by nothing but the server's read cap.
 */
const DEFAULT_QUERY_BATCH_SIZE = 500;

/**
 * Length at which a GET read URL is considered too long to be safe through
 * Kong and restify. Matches the threshold the web Select2 reader uses to swap
 * a GET for the equivalent POST.
 */
const DEFAULT_URL_LENGTH_BUDGET = 1800;

/**
 * How many entities are resolved concurrently.
 */
const DEFAULT_ENTITY_CONCURRENCY = 4;

/**
 * A `~` separates filter terms and a `,` separates the members of an IN list,
 * and both are split *after* the transport layer has URL-decoded the filter
 * once. A GUID containing either character therefore cannot travel inside an IN
 * list on any route, and has to fall back to a single-GUID lookup.
 */
const UNBATCHABLE_GUID_PATTERN = /[,~]/;

const defaultHeadlightIntegrationGUIDBatchResolverOptions = (
	{
		BatchSize: DEFAULT_BATCH_SIZE,
		QueryBatchSize: DEFAULT_QUERY_BATCH_SIZE,
		URLLengthBudget: DEFAULT_URL_LENGTH_BUDGET,
		EntityConcurrency: DEFAULT_ENTITY_CONCURRENCY,

		// Read mode for the batch lookup. 'Lite' returns every ID/GUID column
		// (all this needs) instead of every column; 'Reads' returns whole records.
		ReadMode: 'Lite',

		// Route template for the body-driven read, used when the server
		// advertises RetoldMetadata.Capabilities.QueryEndpoint on its schema.
		QueryEndpointRoute: '<Entity>s/Query',

		// Set false to keep every batch on the URI-based GET route.
		UseQueryEndpoint: true
	});

/**
 * @typedef {Object} MappedGUIDReference
 * @property {'External'|'Headlight'|'Destination'} Kind - Which mapped-GUID form the field uses.
 * @property {string} MappedEntityName - The referenced entity.
 * @property {string} [DestinationField] - The ID column the value lands in, when the field name dictates it.
 * @property {boolean} SetMappedEntityIDField - Whether `ID<MappedEntity>` is also written.
 */

/**
 * Classify one source-record field as a mapped-GUID reference, or not a
 * reference at all.
 *
 * This is the single definition of what counts as a foreign key in a
 * comprehension record: the marshal path and the batch prefetch both read it,
 * so they cannot disagree about which fields need resolving.
 *
 * @param {string} pFieldKey - The source record's field name.
 * @param {string} pEntityGUIDName - The owning entity's own GUID column (`GUID<Entity>`), which is never a reference.
 * @return {MappedGUIDReference|null} The reference, or null when the field is not one.
 */
const classifyMappedGUIDField = (pFieldKey, pEntityGUIDName) =>
{
	if (!pFieldKey || typeof(pFieldKey) !== 'string')
	{
		return null;
	}
	if (pFieldKey === pEntityGUIDName || pFieldKey === `_${pEntityGUIDName}`)
	{
		return null;
	}
	if (pFieldKey.startsWith('_Dest_IDEntity_') && pFieldKey.includes('_Via_'))
	{
		const tmpMappedEntityGUIDName = pFieldKey.split('_Via_')[1];
		return (
			{
				Kind: 'Destination',
				MappedEntityName: tmpMappedEntityGUIDName.substring(4),
				DestinationField: pFieldKey.split('_Via_')[0].split('_Dest_IDEntity_')[1],
				SetMappedEntityIDField: false
			});
	}
	if (pFieldKey.startsWith('_Dest_'))
	{
		return null;
	}
	if (pFieldKey.startsWith('GUID'))
	{
		return { Kind: 'External', MappedEntityName: pFieldKey.substring(4), SetMappedEntityIDField: false };
	}
	if (pFieldKey.startsWith('_GUID'))
	{
		return { Kind: 'Headlight', MappedEntityName: pFieldKey.substring(5), SetMappedEntityIDField: true };
	}
	return null;
};

/**
 * Gather every mapped-GUID reference in a set of source records, grouped by the
 * entity being referenced.
 *
 * @param {Array<Record<string, any>>|Record<string, Record<string, any>>} pRecords - The source records.
 * @param {string} pEntityGUIDName - The owning entity's own GUID column.
 * @param {Map<string, Set<string>>} [pReferences] - An existing map to merge into.
 * @return {Map<string, Set<string>>} Referenced entity -> the GUID values referenced.
 */
const collectMappedGUIDReferences = (pRecords, pEntityGUIDName, pReferences) =>
{
	const tmpReferences = pReferences || new Map();
	const tmpRecordList = Array.isArray(pRecords) ? pRecords : Object.values(pRecords || {});

	for (const tmpRecord of tmpRecordList)
	{
		if (!tmpRecord || typeof(tmpRecord) !== 'object')
		{
			continue;
		}
		for (const tmpFieldKey of Object.keys(tmpRecord))
		{
			const tmpReference = classifyMappedGUIDField(tmpFieldKey, pEntityGUIDName);
			if (!tmpReference || !tmpReference.MappedEntityName)
			{
				continue;
			}
			const tmpValue = tmpRecord[tmpFieldKey];
			if (typeof(tmpValue) !== 'string' && typeof(tmpValue) !== 'number')
			{
				continue;
			}
			const tmpGUIDValue = String(tmpValue);
			if (!tmpGUIDValue)
			{
				continue;
			}
			if (!tmpReferences.has(tmpReference.MappedEntityName))
			{
				tmpReferences.set(tmpReference.MappedEntityName, new Set());
			}
			tmpReferences.get(tmpReference.MappedEntityName).add(tmpGUIDValue);
		}
	}

	return tmpReferences;
};

/**
 * Resolves referenced-entity GUIDs to Headlight IDs in batches, filling the
 * shared GUID map so the per-record marshal path finds them in memory instead
 * of issuing one request per reference.
 *
 * Every batch is an IN list against the referenced entity's GUID column, sent
 * either as `GET <Entity>s/Lite/FilteredTo/<filter>/0/<cap>` or, when the
 * server advertises the capability, as the body-driven
 * `POST <Entity>s/Query` — which carries far larger lists because the filter
 * never touches the URI.
 *
 * Values are URI-encoded in both directions: the body-driven read decodes the
 * `Filter` exactly once, mirroring what the HTTP layer does to the equivalent
 * GET path segment.
 */
class HeadlightIntegrationGUIDBatchResolver extends libFableServiceProviderBase
{
	/**
	 * @param {import('fable')} pFable - The Fable instance.
	 * @param {Record<string, any>} [pOptions] - (optional) Options for the resolver.
	 * @param {string} [pServiceHash] - (optional) Hash to use for the service.
	 */
	constructor(pFable, pOptions, pServiceHash)
	{
		const tmpOptions = Object.assign({}, defaultHeadlightIntegrationGUIDBatchResolverOptions, pOptions);
		super(pFable, tmpOptions, pServiceHash);

		/** @type {import('fable') & { HeadlightRestClient: import('headlight-rest-client'), HeadlightGUIDMap: import('./Headlight-Integration-GUIDMap.js') }} */
		this.fable;
		this.serviceType = 'IntegrationGUIDBatchResolver';

		if (!this.fable.hasOwnProperty('HeadlightGUIDMap'))
		{
			this.fable.addAndInstantiateServiceType('HeadlightGUIDMap', require('./Headlight-Integration-GUIDMap.js'));
		}

		/** @type {import('headlight-rest-client')} */
		this._Client = this.options.Client || null;

		/**
		 * Whether the server serves the body-driven read. Left undefined until a
		 * caller sets it from the schema's RetoldMetadata capabilities; the
		 * URI-based GET route is used until then.
		 *
		 * @type {boolean|undefined}
		 */
		this.QueryEndpointSupported = this.options.QueryEndpointSupported;
	}

	/**
	 * @return {import('headlight-rest-client')}
	 */
	get client()
	{
		return this._Client || this.fable.HeadlightRestClient;
	}

	set client(pClient)
	{
		this._Client = pClient;
	}

	/**
	 * @return {import('./Headlight-Integration-GUIDMap.js')}
	 */
	get guidMap()
	{
		return this.fable.HeadlightGUIDMap;
	}

	/**
	 * Adopt the batch-read capability advertised by an entity schema document.
	 * The capability describes the server, not the entity, so any entity's
	 * schema answers for all of them.
	 *
	 * @param {Object} pSchemaDocument - A `/{Entity}/Schema` response.
	 */
	applySchemaCapabilities(pSchemaDocument)
	{
		if (!pSchemaDocument || typeof(pSchemaDocument) !== 'object')
		{
			return;
		}
		const tmpCapabilities = pSchemaDocument.RetoldMetadata && pSchemaDocument.RetoldMetadata.Capabilities;
		if (tmpCapabilities && typeof(tmpCapabilities.QueryEndpoint) === 'boolean')
		{
			this.QueryEndpointSupported = tmpCapabilities.QueryEndpoint;
		}
	}

	/**
	 * @return {boolean} Whether batches should go out on the body-driven read route.
	 */
	get useQueryEndpoint()
	{
		return (this.options.UseQueryEndpoint !== false) && (this.QueryEndpointSupported === true);
	}

	/**
	 * Whether a GUID is already answered by the in-memory maps the marshal path
	 * consults first: the external-GUID map (populated as earlier entities in
	 * this run were pushed) and the Headlight GUID map (populated by upserts and
	 * by earlier batches).
	 *
	 * @param {string} pEntity - The referenced entity.
	 * @param {string} pGUID - The referenced GUID.
	 * @return {boolean} True when a lookup would be answered from memory.
	 */
	isCached(pEntity, pGUID)
	{
		return Boolean(this.guidMap.getHeadlightIDFromExternalGUID(pEntity, pGUID))
			|| Boolean(this.guidMap.getIDFromGUID(pEntity, pGUID));
	}

	/**
	 * Reduce a set of referenced GUIDs to the ones actually worth asking the
	 * server about.
	 *
	 * @param {string} pEntity - The referenced entity.
	 * @param {Set<string>|Array<string>} pGUIDs - The referenced GUIDs.
	 * @return {{ Pending: Array<string>, Cached: number, Unbatchable: Array<string> }} The split.
	 */
	partitionGUIDs(pEntity, pGUIDs)
	{
		/** @type {Array<string>} */
		const tmpPending = [];
		/** @type {Array<string>} */
		const tmpUnbatchable = [];
		let tmpCached = 0;

		for (const tmpGUID of pGUIDs)
		{
			if (!tmpGUID)
			{
				continue;
			}
			if (this.isCached(pEntity, tmpGUID))
			{
				tmpCached++;
				continue;
			}
			if (UNBATCHABLE_GUID_PATTERN.test(tmpGUID))
			{
				tmpUnbatchable.push(tmpGUID);
				continue;
			}
			tmpPending.push(tmpGUID);
		}

		return { Pending: tmpPending, Cached: tmpCached, Unbatchable: tmpUnbatchable };
	}

	/**
	 * Build the meadow IN filter for a batch of GUIDs.
	 *
	 * The instruction must be `FBL` (filter by list): `FBV` binds the whole
	 * comma-joined value as a single scalar, producing `IN ('a,b,c')` -- which
	 * matches nothing and reports no error.
	 *
	 * @param {string} pEntity - The referenced entity.
	 * @param {Array<string>} pGUIDs - The GUIDs in the batch.
	 * @return {string} The filter expression.
	 */
	buildGUIDFilter(pEntity, pGUIDs)
	{
		return `FBL~GUID${pEntity}~INN~${pGUIDs.map(encodeURIComponent).join(',')}`;
	}

	/**
	 * Take the next batch off the front of a pending queue, bounded by the batch
	 * size and — on the URI-based route — by the length of the URL it would
	 * produce.
	 *
	 * @param {string} pEntity - The referenced entity.
	 * @param {Array<string>} pQueue - The pending GUIDs; the returned batch is spliced off it.
	 * @return {Array<string>} The next batch.
	 */
	takeBatch(pEntity, pQueue)
	{
		if (this.useQueryEndpoint)
		{
			return pQueue.splice(0, this.options.QueryBatchSize);
		}

		// `<Entity>s/Lite/FilteredTo/<filter>/0/<cap>` less the filter itself,
		// with room for the server URL prefix the client prepends.
		let tmpBudget = this.options.URLLengthBudget - (pEntity.length * 2) - 48;
		const tmpBatch = [];

		while (pQueue.length > 0 && tmpBatch.length < this.options.BatchSize)
		{
			const tmpCost = encodeURIComponent(pQueue[0]).length + 1;
			if (tmpBatch.length > 0 && tmpCost > tmpBudget)
			{
				break;
			}
			tmpBudget -= tmpCost;
			tmpBatch.push(pQueue.shift());
		}

		return tmpBatch;
	}

	/**
	 * Read one batch of GUIDs.
	 *
	 * @param {string} pEntity - The referenced entity.
	 * @param {Array<string>} pGUIDs - The GUIDs in the batch.
	 * @return {Promise<Array<Record<string, any>>|null>} The records, or null when the body-driven read is unavailable and the batch should be retried on the GET route.
	 */
	async readBatch(pEntity, pGUIDs)
	{
		const tmpFilter = this.buildGUIDFilter(pEntity, pGUIDs);

		if (this.useQueryEndpoint)
		{
			try
			{
				return await this._postQueryBatch(pEntity, tmpFilter, pGUIDs.length);
			}
			catch (pError)
			{
				this.log.warn(`Batch GUID read for [${pEntity}] could not use the body-driven read route; falling back to URI reads: ${pError.message || pError}`);
				this.QueryEndpointSupported = false;
				return null;
			}
		}

		try
		{
			return await this._getFilteredBatch(pEntity, tmpFilter, pGUIDs.length);
		}
		catch (pError)
		{
			this.log.warn(`Batch GUID read for [${pEntity}] failed for ${pGUIDs.length} GUID(s); those references fall back to single lookups: ${pError.message || pError}`);
			return [];
		}
	}

	/**
	 * @param {string} pEntity - The referenced entity.
	 * @param {string} pFilter - The IN filter.
	 * @param {number} pCap - The record cap.
	 * @return {Promise<Array<Record<string, any>>>} The records.
	 * @private
	 */
	_getFilteredBatch(pEntity, pFilter, pCap)
	{
		const tmpReadMode = (this.options.ReadMode === 'Lite') ? '/Lite' : '';
		return new Promise((resolve, reject) =>
		{
			this.client.getJSON(`${pEntity}s${tmpReadMode}/FilteredTo/${pFilter}/0/${pCap}`,
				(pError, pBody) =>
				{
					if (pError)
					{
						return reject(pError);
					}
					return resolve(Array.isArray(pBody) ? pBody : []);
				});
		});
	}

	/**
	 * @param {string} pEntity - The referenced entity.
	 * @param {string} pFilter - The IN filter.
	 * @param {number} pCap - The record cap.
	 * @return {Promise<Array<Record<string, any>>>} The records.
	 * @private
	 */
	_postQueryBatch(pEntity, pFilter, pCap)
	{
		const tmpRoute = this.options.QueryEndpointRoute.replace('<Entity>', pEntity);
		const tmpBody = { Filter: pFilter, Begin: 0, Cap: pCap };
		if (this.options.ReadMode === 'Lite')
		{
			tmpBody.Lite = true;
		}
		return new Promise((resolve, reject) =>
		{
			this.client.postJSON(tmpRoute, tmpBody,
				(pError, pBody) =>
				{
					if (pError)
					{
						return reject(pError);
					}
					if (!Array.isArray(pBody))
					{
						return reject(new Error(`Body-driven read of [${pEntity}] did not return a record array.`));
					}
					return resolve(pBody);
				});
		});
	}

	/**
	 * Resolve one entity's referenced GUIDs, mapping every hit into the shared
	 * GUID map.
	 *
	 * @param {string} pEntity - The referenced entity.
	 * @param {Set<string>|Array<string>} pGUIDs - The referenced GUIDs.
	 * @return {Promise<{ Entity: string, Requested: number, Cached: number, Resolved: number, Missing: Array<string>, Unverified: number, Requests: number }>} What the batch resolved.
	 */
	async resolveEntityGUIDs(pEntity, pGUIDs)
	{
		const tmpPartition = this.partitionGUIDs(pEntity, pGUIDs);
		const tmpResult = (
			{
				Entity: pEntity,
				Requested: tmpPartition.Pending.length,
				Cached: tmpPartition.Cached,
				Resolved: 0,
				Missing: [],
				Unverified: tmpPartition.Unbatchable.length,
				Requests: 0
			});

		if (tmpPartition.Pending.length < 1)
		{
			return tmpResult;
		}

		const tmpGUIDColumn = `GUID${pEntity}`;
		const tmpIDColumn = `ID${pEntity}`;
		const tmpQueue = tmpPartition.Pending.slice();

		while (tmpQueue.length > 0)
		{
			const tmpBatch = this.takeBatch(pEntity, tmpQueue);
			const tmpRecords = await this.readBatch(pEntity, tmpBatch);

			if (tmpRecords === null)
			{
				// The body-driven route dropped out; the batch goes back on the
				// queue and is re-cut for the (smaller) URI route.
				tmpQueue.unshift(...tmpBatch);
				continue;
			}

			tmpResult.Requests++;

			const tmpFound = new Set();
			for (const tmpRecord of tmpRecords)
			{
				if (!tmpRecord || typeof(tmpRecord) !== 'object')
				{
					continue;
				}
				const tmpGUID = tmpRecord[tmpGUIDColumn];
				const tmpID = tmpRecord[tmpIDColumn];
				if (!tmpGUID || !(tmpID > 0))
				{
					continue;
				}
				this.guidMap.mapGUIDToID(pEntity, tmpGUID, tmpID);
				tmpFound.add(tmpGUID);
			}

			if (tmpFound.size < 1)
			{
				// A batch that matches nothing at all is far more likely to be a
				// broken filter or route than a batch of entirely absent records,
				// and a caller that trusted it would skip the single-GUID lookups
				// that would have resolved them.  Report those GUIDs as unverified
				// rather than missing.
				tmpResult.Unverified += tmpBatch.length;
				this.log.warn(`Batch read of ${tmpBatch.length} [${pEntity}] GUID(s) matched no records; those references fall back to single lookups.`);
				continue;
			}

			tmpResult.Resolved += tmpFound.size;
			for (const tmpGUID of tmpBatch)
			{
				if (!tmpFound.has(tmpGUID))
				{
					tmpResult.Missing.push(tmpGUID);
				}
			}
		}

		return tmpResult;
	}

	/**
	 * Resolve a whole map of referenced GUIDs, a few entities at a time.
	 *
	 * @param {Map<string, Set<string>>} pReferences - Referenced entity -> GUID values.
	 * @return {Promise<{ Entities: Array<Object>, Requested: number, Cached: number, Resolved: number, Unverified: number, Requests: number, Missing: Map<string, Set<string>> }>} What the pass resolved.
	 */
	async resolveReferences(pReferences)
	{
		const tmpSummary = { Entities: [], Requested: 0, Cached: 0, Resolved: 0, Unverified: 0, Requests: 0, Missing: new Map() };
		const tmpEntities = Array.from(pReferences.keys()).filter((pEntity) => Boolean(pEntity));
		if (tmpEntities.length < 1)
		{
			return tmpSummary;
		}

		let tmpNextEntityIndex = 0;
		const tmpWorkerCount = Math.max(1, Math.min(this.options.EntityConcurrency, tmpEntities.length));
		const tmpWorkers = [];

		for (let i = 0; i < tmpWorkerCount; i++)
		{
			tmpWorkers.push((async () =>
			{
				while (tmpNextEntityIndex < tmpEntities.length)
				{
					const tmpEntity = tmpEntities[tmpNextEntityIndex++];
					const tmpResult = await this.resolveEntityGUIDs(tmpEntity, pReferences.get(tmpEntity));
					tmpSummary.Entities.push(tmpResult);
					tmpSummary.Requested += tmpResult.Requested;
					tmpSummary.Cached += tmpResult.Cached;
					tmpSummary.Resolved += tmpResult.Resolved;
					tmpSummary.Unverified += tmpResult.Unverified;
					tmpSummary.Requests += tmpResult.Requests;
					if (tmpResult.Missing.length > 0)
					{
						tmpSummary.Missing.set(tmpEntity, new Set(tmpResult.Missing));
					}
				}
			})());
		}

		await Promise.all(tmpWorkers);

		return tmpSummary;
	}
}

module.exports = HeadlightIntegrationGUIDBatchResolver;

module.exports.default_configuration = defaultHeadlightIntegrationGUIDBatchResolverOptions;
module.exports.classifyMappedGUIDField = classifyMappedGUIDField;
module.exports.collectMappedGUIDReferences = collectMappedGUIDReferences;
module.exports.UNBATCHABLE_GUID_PATTERN = UNBATCHABLE_GUID_PATTERN;
